/**
 * Resolves API base URLs for Chronovela client requests.
 *
 * Short operations may use same-origin (Firebase Hosting rewrites).
 * Long-running /api/generate calls must use the direct Cloud Run service URL
 * to avoid Firebase Hosting's immutable 60-second proxy timeout.
 *
 * ORIGINAL_ENGINE_COMPAT and staging preview builds must never use relative /api/*
 * because Hosting preview rewrites reach production Cloud Run.
 */

const CLOUD_RUN_HOST_SUFFIX = '.run.app';

export function isValidCloudRunServiceUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname.endsWith(CLOUD_RUN_HOST_SUFFIX);
  } catch {
    return false;
  }
}

export function normalizeApiBaseUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

function isVitestRuntime(): boolean {
  if (typeof process !== 'undefined' && process.env.VITEST === 'true') return true;
  const env = (import.meta as { env?: Record<string, string | boolean | undefined> }).env;
  return env?.VITEST === true;
}

function readEnv(key: string): string {
  const fromProcess =
    typeof process !== 'undefined' && Object.prototype.hasOwnProperty.call(process.env, key)
      ? (process.env[key] ?? '').trim()
      : undefined;
  const viteVal = (import.meta as { env?: Record<string, string | boolean | undefined> }).env?.[key];
  const fromImportMeta = typeof viteVal === 'string' ? viteVal.trim() : '';

  if (isVitestRuntime() && fromProcess !== undefined) {
    return fromProcess;
  }
  if (fromImportMeta) return fromImportMeta;
  if (fromProcess !== undefined) return fromProcess;
  return '';
}

function isProductionBuild(): boolean {
  const env = (import.meta as { env?: Record<string, string | boolean | undefined> }).env;
  if (env?.PROD === true || env?.MODE === 'production') return true;
  return process.env.NODE_ENV === 'production';
}

/** Compat/staging bundles must call Cloud Run directly — never Hosting /api rewrites. */
export function isCompatDirectApiBuild(): boolean {
  const narrativeMode = readEnv('VITE_NARRATIVE_ENGINE_MODE');
  const stagingMode = readEnv('VITE_STAGING_MODE');
  return narrativeMode === 'ORIGINAL_ENGINE_COMPAT' || stagingMode === 'true';
}

export function resolveLongRunningApiBase(envBase: string, isProd: boolean): string {
  if (!envBase || !isValidCloudRunServiceUrl(envBase)) {
    if (isProd) {
      throw new Error(
        '[apiBase] VITE_API_BASE_URL must be set to the stable Cloud Run service URL ' +
          '(https://<service>-<hash>-<region>.a.run.app) for long-running generation in production.',
      );
    }
    return 'http://localhost:3001';
  }
  return normalizeApiBaseUrl(envBase);
}

/**
 * @param longRunning When true, production builds require VITE_API_BASE_URL
 *                    pointing at the stable Cloud Run service URL.
 */
export function resolveApiBaseUrl(options?: { longRunning?: boolean }): string {
  const envBase = readEnv('VITE_API_BASE_URL');
  const isProd = isProductionBuild();
  const compatDirect = isCompatDirectApiBuild();

  if (options?.longRunning || compatDirect) {
    return resolveLongRunningApiBase(envBase, isProd || compatDirect);
  }

  // Standard generation stays on same-origin Hosting rewrites (avoids bypassing the 60s proxy only where unnecessary).
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return 'http://localhost:3001';
}

export function resolveGenerateUrl(longRunning: boolean): string {
  return `${resolveApiBaseUrl({ longRunning })}/api/generate`;
}

export function resolveProxyStorageUrl(storagePath: string): string {
  return `${resolveApiBaseUrl()}/api/proxy-storage?path=${encodeURIComponent(storagePath)}`;
}

/** Policies that must bypass Firebase Hosting's 60s /api proxy and call Cloud Run directly. */
export function isLongRunningGeneratePolicy(policy: string): boolean {
  return (
    policy === 'sequel_step' ||
    policy === 'major_scene' ||
    policy === 'four_beat_initial' ||
    policy === 'four_beat_repair' ||
    policy === 'original_engine_compat'
  );
}
